import { autoinject } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { I18N } from 'aurelia-i18n';
import { CaseService } from 'app/cm/services/case-service';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation, ConfirmationOptions } from 'app/shared/elements/ess-confirm';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import * as CM from 'app/cm/models/essette-api'

@autoinject()
export class CaseQueuePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected caseQueueId: string = '';
    // To load necessary case queue information
    protected caseQueue: CM.CaseQueue | null = null;
    protected cases: CM.Case[] = [];
    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;

    protected showCaseSummaryLink: boolean = false;
    protected showCaseLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly caseService: CaseService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'detail.dueDate',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected async canActivate(params: { queueId: string }): Promise<boolean> {
        await this.accountContext.load();

        if(!params.queueId) { throw new Error('Missing required parameters: queueId'); }

        const caseQueueRouteCode: string = params.queueId ? `cm.queues.case.${params.queueId}` : '';

        return this.accountContext.hasRouteAccess(caseQueueRouteCode);
    }

    protected async activate(params: { queueId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(!params?.queueId) {
            throw new Error('Missing required parameters: queueId');
        }

        this.caseQueueId = params.queueId;
        await this.reload();

        if(this.caseQueue && routeConfig.navModel) {
            const title = this.i18n.tr('cm:routes.queues.$title', { what: this.caseQueue.$display });
            routeConfig.navModel.setTitle(title);
        }

        this.showCaseSummaryLink = this.accountContext?.hasRouteAccess('cm.case.summary');
        this.showCaseLink = this.accountContext?.hasRouteAccess('cm.case');
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        if(this.caseQueueId) {
            await Promise.all([
                this.loadCaseQueue(this.caseQueueId),
                this.loadCasesByQueue(this.caseQueueId)
            ]);
        }
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        if(direction === null) {
            this.sorting.remove(property);

            if(this.sorting.entries.length === 0) {
                this.sorting.entries = [
                    {
                        reference: 'detail.dueDate',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            this.sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(this.caseQueueId) {
            this.loadCasesByQueue(this.caseQueueId);
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
    }

    protected async returnCase(selectedCase: CM.Case): Promise<void> {
        if(selectedCase.id && this.caseQueueId) {
            const title = this.i18n.tr('ui:actions.return', { what: this.i18n.tr('cm:case.$') });
            const message = this.i18n.tr('cm:local.case.returnCase.$', { what: selectedCase.number });
            const result = await confirmation.showCustom(title, message, ConfirmationOptions.OKCancel)
            if(result.dialogResponse !== confirmation.options.OK) { return; }
            await this.returnFromQueue(selectedCase.id, this.caseQueueId);
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCaseQueue(queueId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.caseService.loadQueue(queueId);
            this.caseQueue = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCasesByQueue(queueId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        const includes: string[] = [
            'acuity',
            'assignedTo',
            'intensity',
            'member',
            'member.plan',
            'metadata.created.byUser',
            'referral.destination',
            'referral.reason',
            'referral.source',
            'status',
            'status.reason'
        ];

        const fields: string[] = [
            'isClosed',
            'acuity.description',
            'assignedTo.description',
            'detail.dueDate',
            'detail.metadata.started.timestamp',
            'intensity.description',
            'member.eligibility.isCurrent',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'metadata.rollupModified.timestamp',
            'metadata.started.timestamp',
            'number',
            'referral.destination.description',
            'referral.reason.description',
            'referral.source.description',
            'status.description',
            'status.reason.description'
        ];

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);

        try {
            this.state.beginLoad();

            const response = await this.caseService.loadCasesByQueue(queueId, options, includes, fields);
            this.cases = response.results;
            this.resultCount = response.count;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async returnFromQueue(caseId: string, caseQueueId: string): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.caseService.returnFromQueue(caseId, caseQueueId);
            isSuccessful = response.isSuccessful;
            if(isSuccessful) {
                await this.reload();
            }
            else {
                throw new Error('Return unsuccessful');
            }
        } catch(error) {
            this.logger.error(`Unable to return case from queue`, error);
        } finally {
            this.toaster.standard.returned('cm:case.$', isSuccessful);
            this.state.endSave();
        }
    }
}
